import AddOrganizationAction from './components/AddOrganizationAction';
import ConfirmEmailAction from './components/ConfirmEmailAction';
import FacePageHeader from '@/components/layout/FacePageHeader';
import LiveTrackingSection from './components/LiveTrackingSection';
import MainPageOnboarding from './components/MainPageOnboarding';
import Nav from '@/components/navigation/Nav';
import PageContentGrid from '@/components/layout/PageContentGrid';
import PageIntro from '@/components/layout/PageIntro';
import PageLoadingState from '@/components/ui/PageLoadingState';
import RequiredActionsSection from './components/RequiredActionsSection';
import ShipmentStatusSummary from './components/ShipmentStatusSummary';
import SupplierPaymentAction from './components/SupplierPaymentAction';
import {
  activeShipmentPipelineStatuses,
  canShowSupplierQrAction,
  getShipmentCounts,
  getShipments
} from '@/features/Shipment';
import { Balance, useClientFinanceSummary } from '@/features/Finance';
import { CoachmarkTarget } from '@/components/ui/Coachmark';
import { formatValue } from '@/lib/formatters';
import { onboardingTargetIds } from '@/features/Onboarding';
import { PageFooter, PageHeader, PageMain, PageShell } from '@/components/layout/PageShell';
import { toast } from '@/components/ui/sonner';
import { useAuthStore } from '@/features/Auth/model/authStore';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOrganization } from '@/features/Organization';
import { useQuery } from '@tanstack/react-query';
import { USER_ACTIONS_UNAVAILABLE_MESSAGE, useUserActions } from '@/features/UserAction';
import { useUserProfileApi } from '@/features/Profile/model/useUserProfileApi';

const MainPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isSupplierPaymentActionVisible = false;
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const tokenData = useAuthStore((state) => state.tokenData);
  const orgId = tokenData?.org_id ?? null;
  const userId = tokenData?.user_id ?? null;

  const { isOrganizationError, isOrganizationLoading, organization } = useOrganization();

  const {
    data: dataUseClientFinanceSummary,
    isError: isErrorUseClientFinanceSummary,
    isLoading: isLoadingUseClientFinanceSummary
  } = useClientFinanceSummary({ orgId });

  const {
    data: dataGetUserById,
    isError: isErrorGetUserById,
    isLoading: isLoadingGetUserById,
    isSuccess: isSuccessGetUserById
  } = useUserProfileApi();

  const {
    data: dataGetShipments,
    isError: isErrorGetShipments,
    isLoading: isLoadingGetShipments
  } = useQuery({
    enabled: Boolean(orgId),
    queryFn: () =>
      getShipments({
        filters: [
          {
            name: 'status',
            value: activeShipmentPipelineStatuses
          }
        ],
        org_id: orgId!
      }),
    queryKey: ['getShipments', location.pathname, orgId, 'active'],
    retry: 1
  });

  const {
    data: dataGetShipmentCounts,
    isError: isErrorGetShipmentCounts,
    isLoading: isLoadingGetShipmentCounts,
    isSuccess: isSuccessGetShipmentCounts,
    refetch: refetchGetShipmentCounts
  } = useQuery({
    enabled: Boolean(orgId),
    queryFn: () =>
      getShipmentCounts({
        org_id: orgId!
      }),
    queryKey: ['getShipmentCounts', location.pathname, orgId],
    refetchOnWindowFocus: false,
    retry: 1
  });

  const {
    data: dataGetUserActions,
    isError: isErrorGetUserActions,
    isLoading: isLoadingGetUserActions
  } = useUserActions({
    isEnabled: Boolean(orgId && userId),
    orgId,
    userId
  });

  const balanceAmount = dataUseClientFinanceSummary?.availableBalance ?? 0;
  const userDisplayName = dataGetUserById?.first_name?.trim() ?? '';
  const clientGreeting = userDisplayName ? `Добрый день, ${userDisplayName}` : 'Добрый день';
  const clientName = formatValue({
    value: organization?.short_name ?? organization?.full_name
  });
  const hasActiveShipments = (dataGetShipments?.total_count ?? 0) > 0;
  const isEmailConfirmationActionVisible = dataGetUserById?.email_verified === false;
  const shipmentCount = dataGetShipmentCounts
    ? dataGetShipmentCounts.active_count + dataGetShipmentCounts.completed_count
    : null;
  const isBalanceUnavailable = !orgId || !dataUseClientFinanceSummary || isErrorUseClientFinanceSummary;
  const isMainPageLoading =
    isLoadingUseClientFinanceSummary ||
    isOrganizationLoading ||
    isLoadingGetShipments ||
    isLoadingGetShipmentCounts ||
    isLoadingGetUserById ||
    isLoadingGetUserActions;
  const filteredUserActions = useMemo(() => {
    return (dataGetUserActions ?? []).filter((action) => {
      if (action.code !== 'SEND_QR_TO_SUPPLIER') return true;

      const shipment = dataGetShipments?.items.find((item) => item.shipment_id === action.subject.id);

      return canShowSupplierQrAction(shipment?.status);
    });
  }, [dataGetShipments?.items, dataGetUserActions]);

  useEffect(() => {
    if (!isErrorUseClientFinanceSummary) return;

    toast.error('Баланс недоступен');
  }, [isErrorUseClientFinanceSummary]);

  useEffect(() => {
    if (!isOrganizationError) return;

    toast.error('Организация недоступна');
  }, [isOrganizationError]);

  useEffect(() => {
    if (!isErrorGetShipments) return;

    toast.error('Поставки недоступны');
  }, [isErrorGetShipments]);

  useEffect(() => {
    if (!isErrorGetShipmentCounts) return;

    toast.error('Счётчики поставок недоступны');
  }, [isErrorGetShipmentCounts]);

  useEffect(() => {
    if (!isErrorGetUserById) return;

    toast.error('Пользователь недоступен');
  }, [isErrorGetUserById]);

  useEffect(() => {
    if (!isErrorGetUserActions) return;

    toast.error(USER_ACTIONS_UNAVAILABLE_MESSAGE);
  }, [isErrorGetUserActions]);

  useEffect(() => {
    if (!orgId) return;

    const onPageActivated = (): void => {
      if (document.visibilityState !== 'visible') return;

      void refetchGetShipmentCounts({
        cancelRefetch: false
      });
    };
    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted) return;

      onPageActivated();
    };

    document.addEventListener('visibilitychange', onPageActivated);
    window.addEventListener('focus', onPageActivated);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onPageActivated);
      window.removeEventListener('focus', onPageActivated);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [orgId, refetchGetShipmentCounts]);

  const onClickSummaryBalance = (): void => {
    navigate('/finance');
  };

  return (
    <PageShell contentVariant="face" footerMode="fixed" headerMode="static">
      <PageHeader>
        <FacePageHeader />
      </PageHeader>

      <PageMain contentClassName="flex flex-1 flex-col gap-6">
        {isMainPageLoading ? (
          <PageLoadingState label="Загрузка главной страницы" />
        ) : (
          <>
            <PageIntro
              aside={
                <Balance
                  amount={balanceAmount}
                  currency={dataUseClientFinanceSummary?.currency}
                  isError={isBalanceUnavailable}
                  variant="compact"
                />
              }
              eyebrow={clientGreeting}
              title={clientName}
            />
            {(isEmailConfirmationActionVisible || !orgId) && (
              <CoachmarkTarget className="flex flex-col gap-2" targetId={onboardingTargetIds.accountSetup}>
                {isEmailConfirmationActionVisible && <ConfirmEmailAction email={dataGetUserById?.email} />}
                {!orgId && <AddOrganizationAction />}
              </CoachmarkTarget>
            )}
            <PageContentGrid
              aside={
                <>
                  <Balance
                    amount={balanceAmount}
                    currency={dataUseClientFinanceSummary?.currency}
                    isError={isBalanceUnavailable}
                    onClick={onClickSummaryBalance}
                    variant="summary"
                  />
                  {isSupplierPaymentActionVisible && <SupplierPaymentAction />}
                </>
              }
            >
              <ShipmentStatusSummary
                atWarehouseCount={dataGetShipmentCounts?.warehouse_count ?? 0}
                customsCount={dataGetShipmentCounts?.customs_count ?? 0}
                inTransitCount={dataGetShipmentCounts?.in_transit_count ?? 0}
              />
              <LiveTrackingSection
                isError={isErrorGetShipments}
                isLoading={isLoadingGetShipments}
                items={dataGetShipments?.items ?? []}
              />
              {hasActiveShipments && (
                <RequiredActionsSection isError={isErrorGetUserActions} items={filteredUserActions} />
              )}
              {isSupplierPaymentActionVisible && (
                <div className="min-[769px]:hidden">
                  <SupplierPaymentAction />
                </div>
              )}
            </PageContentGrid>
          </>
        )}
      </PageMain>

      <PageFooter>
        <Nav />
      </PageFooter>
      <MainPageOnboarding
        hasOrganization={Boolean(orgId)}
        isAuthenticated={isAuthenticated}
        isEmailVerified={dataGetUserById?.email_verified === true}
        isProfileError={isErrorGetUserById}
        isProfileLoading={isLoadingGetUserById}
        isProfileSuccess={isSuccessGetUserById}
        isShipmentsError={isErrorGetShipmentCounts}
        isShipmentsLoading={isLoadingGetShipmentCounts}
        isShipmentsSuccess={isSuccessGetShipmentCounts}
        shipmentCount={shipmentCount}
        userId={userId}
      />
    </PageShell>
  );
};

export default MainPage;